import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import React from "react";
import { OrganizationService } from "services/OrganizationService";

type Option = {
    id: string;
    label: string;
};

const flattenedHierarchyOptionsCache = new Map<
    string,
    Option[] | Promise<Option[]>
>();

type Props = {
    orgId: string;
    value: string[];
    onChange: (val: string[]) => void;
    error?: boolean;
    helperText?: string;
    fullWidth?: boolean;
    label?: string;
    placeholder?: string;
    maxContainerHeight?: string;
    isDisabled?: boolean;
};

const ServiceHierarchySelect: React.FC<Props> = ({
    orgId,
    value,
    onChange,
    error,
    helperText,
    fullWidth,
    label,
    placeholder,
    maxContainerHeight,
    isDisabled,
}) => {
    const [options, setOptions] = React.useState<Option[]>();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const handleHierarchyChange = (val?: Option[]) => {
        const values = val?.map((item) => item.id);
        onChange(values || []);
    };

    React.useEffect(() => {
        let destroyed = false;

        async function getFlattenedHierarchyOptions(orgId: string) {
            const cachedOptions = flattenedHierarchyOptionsCache.get(orgId);

            if (Array.isArray(cachedOptions)) {
                setOptions(cachedOptions);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);

            const optionsPromise =
                cachedOptions ??
                new OrganizationService().getFlattenedServiceHierarchy(orgId);

            flattenedHierarchyOptionsCache.set(orgId, optionsPromise);

            const response = await optionsPromise;

            flattenedHierarchyOptionsCache.set(orgId, response);

            if (!destroyed) {
                setOptions(response);
                setIsLoading(false);
            }
        }

        if (orgId) {
            getFlattenedHierarchyOptions(orgId);
        }

        return () => {
            destroyed = true;
        };
    }, [orgId]);

    return (
        <div style={{ width: "100%" }}>
            <AcxSelectMulti
                id="hierarchyOptions"
                options={options || []}
                valueField="id"
                labelField="label"
                inputLabel={label ?? "Hierarchy Structure Members"}
                skeleton={isLoading}
                defaultValue={options?.filter((opt) => value.includes(opt.id))}
                onChange={handleHierarchyChange}
                containerHeight="auto"
                maxContainerHeight={maxContainerHeight}
                error={error}
                helperText={helperText}
                showAllErrors={true}
                fullWidth={fullWidth}
                placeholder={placeholder}
                isDisabled={isDisabled}
            />
        </div>
    );
};

export default ServiceHierarchySelect;
